/* eslint-disable @typescript-eslint/ban-ts-comment */
import type { HTMLAttributes } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { getCurrency } from '@common/utils/currency.ts'
import { format } from 'date-fns'
import { useUnit } from 'effector-react'
import { useQueryClient } from 'react-query'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs.tsx'
import { Tag } from '@/components/ui/tag.tsx'
import { TextLabel } from '@/components/ui/text-label.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { $account } from '@/models/account.ts'
import { loadCreatorsFx } from '@/models/creators.ts'
import { DiscountBadge } from '@/modules/campaigns/components/discount-badge.tsx'
import { useCampaigns } from '@/modules/campaigns/state/hooks.ts'
import type { Creator } from '@/modules/creators/types.ts'
import type { PotentialUpgrade } from '@/modules/licenses/utils.ts'
import { getCurrentPlan } from '@/modules/licenses/utils.ts'
import { handleError } from '@/utils/error.ts'

import { useUpgradeLicenses } from '../../hooks/use-upgrade-licenses.tsx'
import type { BillingPlan, UpgradeLicenseInfo } from '../../types'
import { LicenseMode } from '../../types'
import { LicenseBenefitsPopover } from '../license-benefits-popover.tsx'

interface UpgradeLicenseDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
  clickedUpgrade: PotentialUpgrade
  creator: Creator
  potentialUpgrades: PotentialUpgrade[]
}

export const UpgradeLicenseDialog = (props: UpgradeLicenseDialogProps) => {
  const account = useUnit($account)
  const { open, setOpen, clickedUpgrade, creator, potentialUpgrades } = props
  const hasMonthlyUpgrades = potentialUpgrades.some(
    (upgrade) => upgrade.interval === 'monthly',
  )
  const hasYearlyUpgrades = potentialUpgrades.some(
    (upgrade) => upgrade.interval === 'yearly',
  )

  const queryClient = useQueryClient()
  const toast = useToast()
  const [selectedPlan, setSelectedPlan] = useState<BillingPlan | undefined>(
    clickedUpgrade.plan,
  )
  const [currentToggle, setCurrentToggle] = useState<string>(
    clickedUpgrade.interval,
  )
  const {
    plans,
    licenses,
    upgradePreview,
    isFetchingPreview,
    upgradeMutation,
    planPriceId,
    upgradePreviewError,
  } = useUpgradeLicenses(creator.id, selectedPlan, currentToggle, open)
  const currentPlan = getCurrentPlan(creator, licenses!, plans)
  const { currentDiscount } = useCampaigns()

  useEffect(() => {
    if (!upgradePreviewError) {
      return
    }

    // @ts-ignore
    if (upgradePreviewError.response?.status === 400) {
      // @ts-ignore
      toast.error(upgradePreviewError.response?.data?.detail)
    } else {
      toast.error(
        `There was an error with upgrading your license. ${
          // @ts-ignore
          upgradePreviewError.response
            ? // @ts-ignore
              (upgradePreviewError.response.status ?? 0)
            : ''
        }`,
      )
    }

    setOpen(false)
  }, [setOpen, toast, upgradePreviewError])

  if (!currentPlan) {
    return
  }

  const handleToggleChange = (value: string) => setCurrentToggle(value)

  const handlePlanChange = (planName: string) => {
    if (!plans) return
    setSelectedPlan(plans.find((l) => l.name === planName))
  }

  const handleSubmit = () => {
    if (!upgradePreview || !planPriceId) return

    upgradeMutation
      .mutateAsync({
        anticipatedPrice: upgradePreview.paymentAmount,
        creatorId: creator.id,
        planPrice: planPriceId,
        quantity: 1,
      })
      .then((response) => {
        if (response.paymentSuccessful) {
          toast.success('Your upgraded license is ready for use')
        } else {
          toast.error(
            'Your payment failed. Please pay the invoice to continue.',
          )
          queryClient.invalidateQueries(['owner', account?.userId])
        }
      })
      .catch((error) => {
        handleError(error, 'There was an error with upgrading your license.')
      })
      .finally(() => {
        setOpen(false)
        upgradeMutation.reset()
        loadCreatorsFx()
        queryClient.invalidateQueries('licenses')
        queryClient.invalidateQueries('creator')
      })
  }

  const tabsCount = hasMonthlyUpgrades && hasYearlyUpgrades ? 2 : 1

  return (
    <Dialog
      show={open}
      onClose={() => setOpen(false)}
      bigDialog={true}
    >
      <Dialog.Header>
        <Dialog.Title>Upgrade License: {creator.name}</Dialog.Title>
        <Dialog.Description className="font-medium">
          <span className="mb-4 flex justify-center whitespace-pre">
            You&apos;re currently using the
            <span className="text-primary-500">
              {' '}
              {currentPlan.displayName} License
            </span>
            .
          </span>
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Description className="font-medium">
        {!plans || !selectedPlan ? (
          <>Failed to load...</>
        ) : (
          <Tabs
            defaultValue={currentToggle}
            onValueChange={handleToggleChange}
            className="flex w-full flex-col justify-center"
          >
            <TabsList
              className={
                'w-[400px] self-center ' +
                (tabsCount === 2 ? 'grid grid-cols-2' : '')
              }
            >
              {hasMonthlyUpgrades && (
                <TabsTrigger value={LicenseMode.MONTHLY}>Monthly</TabsTrigger>
              )}
              {hasYearlyUpgrades && (
                <TabsTrigger
                  value={LicenseMode.YEARLY}
                  className={'gap-2 ' + (tabsCount === 1 ? 'w-full' : '')}
                >
                  Yearly
                  <Tag className="h-fit bg-primary-400 text-white dark:bg-primary-500">
                    -25%
                  </Tag>
                </TabsTrigger>
              )}
            </TabsList>
            {isFetchingPreview || !upgradePreview ? (
              <Skeleton className="mt-3 h-80 w-full" />
            ) : (
              <>
                {hasMonthlyUpgrades && (
                  <TabsContent
                    value={LicenseMode.MONTHLY}
                    className="w-full"
                  >
                    <UpgradeLicenseForm
                      licenseMode={LicenseMode.MONTHLY}
                      potentialUpgrades={potentialUpgrades}
                      activePlanName={currentPlan.displayName}
                      selectedPlan={selectedPlan}
                      setSelectedPlan={handlePlanChange}
                      upgradePreview={upgradePreview}
                      blackFridayDiscount={currentDiscount}
                    />
                  </TabsContent>
                )}
                {hasYearlyUpgrades && (
                  <TabsContent value={LicenseMode.YEARLY}>
                    <UpgradeLicenseForm
                      licenseMode={LicenseMode.YEARLY}
                      potentialUpgrades={potentialUpgrades}
                      activePlanName={currentPlan.displayName}
                      selectedPlan={selectedPlan}
                      setSelectedPlan={handlePlanChange}
                      upgradePreview={upgradePreview}
                    />
                  </TabsContent>
                )}
              </>
            )}
          </Tabs>
        )}
      </Dialog.Description>

      <Dialog.ClosableFooter onClose={() => setOpen(false)}>
        <Button
          isLoading={upgradeMutation.isLoading}
          onClick={handleSubmit}
        >
          Upgrade Now
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}

interface UpgradeLicenseFormProps {
  upgradePreview: UpgradeLicenseInfo
  licenseMode: LicenseMode
  activePlanName: string
  selectedPlan: BillingPlan
  setSelectedPlan: (licenseName: string) => void
  potentialUpgrades: PotentialUpgrade[]
  blackFridayDiscount?: number
}

const UpgradeLicenseForm = ({
  upgradePreview,
  licenseMode,
  activePlanName,
  selectedPlan,
  setSelectedPlan,
  potentialUpgrades,
  blackFridayDiscount,
}: UpgradeLicenseFormProps) => {
  const potentialUpgradeValues = useMemo(
    () => potentialUpgrades.filter((p) => p.interval === licenseMode),
    [potentialUpgrades, licenseMode],
  )

  // Setting if the selected plan is not available
  useEffect(
    () => {
      if (
        potentialUpgradeValues.length > 0 &&
        !potentialUpgradeValues.some((p) => p.plan.name === selectedPlan.name)
      ) {
        setSelectedPlan(potentialUpgradeValues[0].plan.name)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [potentialUpgradeValues, selectedPlan],
  )

  const renderAmountWithDiscount = useCallback(
    (amount: string, amountAfterDiscount: string) => {
      if (!blackFridayDiscount || amount === amountAfterDiscount) {
        return amount
      }

      return (
        <div className="flex gap-2">
          <span className="text-danger-default">{amountAfterDiscount}</span>
          <span className="line-through">{amount}</span>
        </div>
      )
    },
    [blackFridayDiscount],
  )

  const {
    currentPayment,
    currentPaymentAfterDiscount,
    balance,
    credit,
    payment,
    nextPayment,
  } = useMemo(() => {
    const amounts = {
      currentPayment: upgradePreview.planPrice.price,
      currentPaymentAfterDiscount: upgradePreview.dueNowIncludingTax / 100,
      credit: upgradePreview.creditIncludingTax / 100,
      balance: upgradePreview.customerBalance / 100,
      payment: upgradePreview.paymentAmount / 100,
      nextPayment: upgradePreview.planPrice.price,
    }

    return Object.entries(amounts).reduce(
      (acc, [key, value]) => {
        acc[key as keyof typeof amounts] = getCurrency(value, 2)

        return acc
      },
      {} as Record<keyof typeof amounts, string>,
    )
  }, [upgradePreview])

  return (
    <div>
      <div className="grid gap-3">
        <TextLabel className="text-title">Selected Plan</TextLabel>

        <div className="flex">
          <Select
            value={selectedPlan.name}
            onValueChange={setSelectedPlan}
          >
            {potentialUpgradeValues.length >= 2 && (
              <SelectTrigger>
                <SelectValue className="flex flex-row justify-between" />
              </SelectTrigger>
            )}
            {potentialUpgradeValues.length <= 1 && (
              <ReadOnlyDropdown>
                <SelectValue className="flex flex-row justify-between" />
              </ReadOnlyDropdown>
            )}
            <SelectContent>
              {potentialUpgradeValues.map((item) => (
                <SelectItem
                  key={item.plan.name}
                  value={item.plan.name}
                  className="flex flex-1"
                >
                  {item.plan.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <LicenseBenefitsPopover
            license={selectedPlan}
            className={
              potentialUpgradeValues.length <= 1 ? 'right-[24px]' : 'right-16'
            }
          />
        </div>
      </div>

      <div className="divide-y-[1px] divide-border">
        <div className="grid gap-4 py-6">
          <TextLabel className="text-title">License Costs</TextLabel>
          <div className="flex justify-between space-x-3">
            <div className="text-sm">
              <span>
                {selectedPlan.displayName} license (
                {format(
                  new Date(upgradePreview.dueNowPeriodStart),
                  'dd MMM yyyy',
                )}{' '}
                -{' '}
                {format(
                  new Date(upgradePreview.dueNowPeriodEnd),
                  'dd MMM yyyy',
                )}
                )
              </span>{' '}
              {!!blackFridayDiscount && (
                <DiscountBadge className="inline-block px-2 py-0.5" />
              )}
            </div>
            <div className="text-right">
              {renderAmountWithDiscount(
                currentPayment,
                currentPaymentAfterDiscount,
              )}
            </div>
          </div>
          {upgradePreview.creditIncludingTax <= 0 && (
            <div className="flex justify-between">
              <div className="text-sm">
                Unused {activePlanName} license (
                {format(
                  new Date(upgradePreview.creditPeriodStart),
                  'dd MMM yyyy',
                )}{' '}
                -{' '}
                {format(
                  new Date(upgradePreview.creditPeriodEnd),
                  'dd MMM yyyy',
                )}
                )
              </div>
              <div className="text-right">{credit}</div>
            </div>
          )}
          {upgradePreview.customerBalance !== 0 && (
            <div className="flex justify-between">
              <p className="text-sm">Your balance</p>

              <p className="text-right">{balance}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between py-6">
          <div className="grid gap-1">
            <TextLabel className="text-title">Total cost for upgrade</TextLabel>
            <p className="text-sm">The amount you will be charged today</p>
          </div>

          <p className="text-right">{payment}</p>
        </div>

        <div className="flex items-center justify-between py-6">
          <div className="grid gap-1">
            <TextLabel className="text-title">Total future cost</TextLabel>
            <p className="text-sm">
              The amount you will be charged {licenseMode} from{' '}
              {format(new Date(upgradePreview.dueNowPeriodEnd), 'dd MMM yyyy')}
            </p>
          </div>
          <p className="text-right">
            {nextPayment} / {licenseMode.substring(0, licenseMode.length - 2)}
          </p>
        </div>
      </div>
    </div>
  )
}

const ReadOnlyDropdown = ({ children }: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className="placeholder:text-muted-foreground flex h-10 w-full items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-left text-sm ring-offset-background  disabled:cursor-not-allowed disabled:opacity-50 dark:text-white">
      <div className="flex-1 truncate">{children}</div>
    </div>
  )
}
